import { getDBAsync } from '@/db';
import type { Player } from '@/types';
import { generateId } from '@/lib/utils';

export const playerRepo = {
  /**
   * Get all players
   */
  async getAll(): Promise<Player[]> {
    const db = await getDBAsync();
    return db.getAll('players');
  },

  /**
   * Get a player by ID
   */
  async getById(id: string): Promise<Player | undefined> {
    const db = await getDBAsync();
    return db.get('players', id);
  },

  /**
   * Create a new player
   */
  async create(name: string): Promise<Player> {
    const db = await getDBAsync();
    const now = new Date();
    const player: Player = {
      id: generateId(),
      name: name.trim(),
      createdAt: now,
      updatedAt: now,
    };
    await db.put('players', player);
    return player;
  },

  /**
   * Update a player
   */
  async update(id: string, name: string): Promise<Player | null> {
    const db = await getDBAsync();
    const existing = await db.get('players', id);
    if (!existing) {
      return null;
    }

    const updated: Player = {
      ...existing,
      name: name.trim(),
      updatedAt: new Date(),
    };
    await db.put('players', updated);
    return updated;
  },

  /**
   * Delete a player
   */
  async delete(id: string): Promise<boolean> {
    const db = await getDBAsync();
    const existing = await db.get('players', id);
    if (!existing) {
      return false;
    }
    await db.delete('players', id);
    return true;
  },

  /**
   * Search players by name
   */
  async searchByName(query: string): Promise<Player[]> {
    const db = await getDBAsync();
    const all = await db.getAll('players');
    const lowerQuery = query.toLowerCase();
    return all.filter((p) => p.name.toLowerCase().includes(lowerQuery));
  },

  /**
   * Check if a player name already exists
   */
  async nameExists(name: string, excludeId?: string): Promise<boolean> {
    const db = await getDBAsync();
    const all = await db.getAll('players');
    const lowerName = name.toLowerCase().trim();
    return all.some(
      (p) => p.name.toLowerCase() === lowerName && p.id !== excludeId
    );
  },
};
